// =============================================================================
// Handwriting Phase 3 — character-level candidate alignment (design §4).
//
// Needleman–Wunsch global alignment over CODE POINTS (match +1, mismatch −1,
// gap −1), reduced to per-string spans of the positions that DIFFER. The
// output is deliberately "a list of character spans to mark", not "a
// candidate pair": the VS renderer consumes (text, spans), so OCR's filed
// per-character-confidence backlog item (charConfidences on RegionResult)
// can later feed the SAME renderer with confidence-derived spans — no
// reshape needed.
//
// Positions index code points (Array.from segmentation) — renderers must
// segment identically.
// =============================================================================
const MATCH = 1;
const MISMATCH = -1;
const GAP = -1;
/** Merge a sorted list of single positions into contiguous spans. */
function toSpans(positions) {
    const spans = [];
    for (const p of positions) {
        const last = spans[spans.length - 1];
        if (last && last.end === p) {
            last.end = p + 1;
        }
        else {
            spans.push({ start: p, end: p + 1 });
        }
    }
    return spans;
}
/**
 * Align two candidate strings and return the differing positions of each,
 * merged into contiguous spans. Identical strings ⇒ two empty lists.
 * Field-sized inputs only (O(n·m) time and memory).
 */
export function alignCandidates(a, b) {
    const aChars = Array.from(a);
    const bChars = Array.from(b);
    const n = aChars.length;
    const m = bChars.length;
    if (n === 0 && m === 0)
        return { aSpans: [], bSpans: [] };
    if (n === 0)
        return { aSpans: [], bSpans: [{ start: 0, end: m }] };
    if (m === 0)
        return { aSpans: [{ start: 0, end: n }], bSpans: [] };
    // DP score matrix, (n+1) x (m+1).
    const score = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++)
        score[i][0] = i * GAP;
    for (let j = 1; j <= m; j++)
        score[0][j] = j * GAP;
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const diag = score[i - 1][j - 1] + (aChars[i - 1] === bChars[j - 1] ? MATCH : MISMATCH);
            const up = score[i - 1][j] + GAP;
            const left = score[i][j - 1] + GAP;
            score[i][j] = Math.max(diag, up, left);
        }
    }
    // Traceback — collect differing positions. Tie-break order (diag, then up,
    // then left) keeps substitutions preferred over gap pairs.
    const aDiff = [];
    const bDiff = [];
    let i = n;
    let j = m;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0) {
            const isMatch = aChars[i - 1] === bChars[j - 1];
            if (score[i][j] === score[i - 1][j - 1] + (isMatch ? MATCH : MISMATCH)) {
                if (!isMatch) {
                    aDiff.push(i - 1);
                    bDiff.push(j - 1);
                }
                i--;
                j--;
                continue;
            }
        }
        if (i > 0 && score[i][j] === score[i - 1][j] + GAP) {
            aDiff.push(i - 1); // deletion — a has a char b lacks
            i--;
            continue;
        }
        bDiff.push(j - 1); // insertion — b has a char a lacks
        j--;
    }
    aDiff.reverse();
    bDiff.reverse();
    return { aSpans: toSpans(aDiff), bSpans: toSpans(bDiff) };
}
//# sourceMappingURL=candidate-spans.js.map